export default class FileManager {
  constructor(controller) {
    this.controller = controller
  }

  previewFiles(event) {
    const newFiles = Array.from(event.target.files)

    if (newFiles.length === 0) {
      this.controller.previewManager.hidePreviewContainer()
      return
    }

    // For new records, we need to maintain all files
    if (this.controller.isNewRecord()) {
      this.addFilesToStoredFiles(newFiles)
      this.updateFileInput()
      this.updateSelectedFilesMessage(this.controller.fileInputTarget.files)
    } else {
      // For existing records, just use the new files
      this.updateSelectedFilesMessage(newFiles)
    }

    // Clear previews and recreate them for all files
    if (this.controller.isNewRecord()) {
      this.controller.previewManager.recreateAllPreviews()
    } else {
      this.controller.previewManager.clearPreviews()
      this.controller.previewManager.showPreviewContainer()
      this.processFiles(newFiles)
    }

    // Only clear file input for existing records (async upload)
    if (!this.controller.isNewRecord()) {
      this.clearFileInput(event.target)
    }
  }

  handleDroppedFiles(imageFiles) {
    if (this.controller.isNewRecord()) {
      this.addFilesToStoredFiles(imageFiles)
      this.updateFileInput()
      this.updateSelectedFilesMessage(this.controller.fileInputTarget.files)
    } else {
      this.updateSelectedFilesMessage(imageFiles)
    }

    if (this.controller.isNewRecord()) {
      this.controller.previewManager.recreateAllPreviews()
    } else {
      this.controller.previewManager.clearPreviews()
      this.controller.previewManager.showPreviewContainer()
      this.processFiles(imageFiles)
    }
  }

  addFilesToStoredFiles(newFiles) {
    newFiles.forEach(newFile => {
      if (!this.isDuplicateFile(newFile)) {
        this.controller.storedFiles.push(newFile)
      }
    })
  }

  isDuplicateFile(file) {
    return this.controller.storedFiles.some(existingFile =>
      existingFile.name === file.name &&
      existingFile.size === file.size &&
      existingFile.lastModified === file.lastModified
    )
  }

  updateFileInput() {
    const dt = new DataTransfer()
    this.controller.storedFiles.forEach(file => {
      dt.items.add(file)
    })
    this.controller.fileInputTarget.files = dt.files
  }

  async processFiles(files) {
    const uploadPromises = []

    Array.from(files).forEach(file => {
      const previewElement = this.controller.previewManager.createPreviewElement(file)
      if (previewElement) {
        if (this.controller.isNewRecord()) {
          this.handleFileForNewRecord(file, previewElement)
        } else {
          uploadPromises.push(this.controller.fileUploader.uploadFile(file, previewElement))
        }
      }
    })

    // Wait for all uploads to complete, then reload iframe
    if (uploadPromises.length > 0) {
      await Promise.all(uploadPromises)
      this.controller.iframeReloader.reloadPreviewIframe()
    }
  }

  handleFileForNewRecord(file, previewElement) {
    const fileData = {
      id: this.generateTempId(),
      filename: file.name,
      byte_size: file.size,
      url: null,
      isTemporary: true
    }

    this.controller.fileUploader.handleUploadSuccess(fileData, previewElement)
  }

  removeTempFile(event) {
    event.preventDefault()

    const button = event.currentTarget
    const filename = button.dataset.filename
    const row = button.closest('tr')

    this.controller.storedFiles = this.controller.storedFiles.filter(file => file.name !== filename)

    this.removeFileFromInput(filename)

    if (row) {
      row.remove()
    }

    this.controller.positionManager.updateAllPositionNumbers()
  }

  removeFileFromInput(filename) {
    const dt = new DataTransfer()

    Array.from(this.controller.fileInputTarget.files).forEach(file => {
      if (file.name !== filename) {
        dt.items.add(file)
      }
    })

    this.controller.fileInputTarget.files = dt.files
  }

  clearFileInput(input) {
    input.value = ""
  }

  updateSelectedFilesMessage(files) {
    if (!this.controller.hasSelectedFilesMessageTarget || files.length === 0) return

    const count = files.length
    const text = count === 1 ? this.controller.getFileText() : this.controller.getFilesText()
    this.controller.selectedFilesMessageTarget.textContent = `${count} ${text}`
  }

  clearSelectedFilesMessage() {
    if (this.controller.hasSelectedFilesMessageTarget) {
      this.controller.selectedFilesMessageTarget.textContent = ''
    }
  }

  findFileByName(filename) {
    return Array.from(this.controller.fileInputTarget.files).find(file => file.name === filename)
  }

  isFileInInput(file) {
    return Array.from(this.controller.fileInputTarget.files).some(existingFile =>
      existingFile.name === file.name &&
      existingFile.size === file.size &&
      existingFile.lastModified === file.lastModified
    )
  }

  generateTempId() {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
}